import { createFileRoute, Outlet, redirect, useRouterState } from '@tanstack/react-router'

import { isAdminAuthFailure } from '@gto/api-client'
import { sessionStore } from '@gto/session'

import { meQueryOptions } from '@/auth/me'
import { canViewSection, sectionForPath } from '@/auth/sections'
import { AdminHeader } from '@/components/admin-header'
import { AdminNav } from '@/components/admin-nav'
import { isForbidden } from '@/utils/be-error-detail'
import { clearAdminSession } from '@/utils/session'

export const MAIN_APP_URL = 'https://gtowizard.com'

export const Route = createFileRoute('/_layout')({
  component: AdminLayout,
  beforeLoad: async ({ context, location }) => {
    if (sessionStore.getState().accessToken === null) {
      throw redirect({ to: '/login', search: { from: location.href } })
    }

    const me = await context.queryClient
      .ensureQueryData(meQueryOptions())
      .catch((error: unknown): never => {
        if (isAdminAuthFailure(error)) {
          throw redirect({ to: '/login', search: { from: location.href } })
        }

        if (isForbidden(error)) {
          clearAdminSession()
          context.queryClient.clear()

          throw redirect({ href: MAIN_APP_URL, replace: true })
        }

        throw error
      })

    const section = sectionForPath(location.pathname)
    if (section !== undefined && !canViewSection(me, section)) {
      throw redirect({ to: '/', replace: true })
    }

    return { me }
  },
})

function AdminLayout() {
  const pathname = useRouterState({ select: (s) => s.location.pathname })
  // The home / picker page is the entry point; the side-nav would be a
  // redundant nav alongside the picker cards. Hide it there.
  const isHomePage = pathname === '/'

  return (
    <div
      className="flex size-full min-h-0 min-w-0 flex-col gap-2.5 bg-paper-elevation0 p-3"
      data-tst="admin-layout"
    >
      <AdminHeader />
      <div className="flex min-h-0 min-w-0 flex-1 flex-row gap-2.5">
        {!isHomePage && (
          <aside className="flex w-[200px] shrink-0 flex-col gap-2 rounded-md bg-paper-elevation1 p-3">
            <AdminNav />
          </aside>
        )}

        {/* `min-h-0` lets this flex child shrink below its content height, so its
            own `overflow-auto` can engage instead of the content pushing the
            layout taller. Don't delete it while chasing an overflow bug: it is
            necessary but not yet sufficient — `#root` is `flex: 1 0 auto` (see
            app.css) inside a `min-height`-only body, so nothing caps the layout
            at the viewport and long pages still scroll the document. */}
        <section className="flex min-h-0 min-w-0 flex-1 flex-col overflow-auto rounded-md bg-paper-elevation1 p-4">
          <Outlet />
        </section>
      </div>
    </div>
  )
}
